import React, { useEffect, useState } from 'react';
import { Icon } from './ui';
import { PolicyVersion, listPolicyVersions } from '../lib/agentsApi';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface PolicyVersionSelectorProps {
  apiUrl: string;
  value: string | null;          // null → inherit platform default
  onChange: (versionId: string | null) => void;
}

/** Short human summary of a policy version's config for the rules preview. */
function summarize(config: PolicyVersion['config']): string[] {
  const out: string[] = [];
  if (config.model_routing && Object.keys(config.model_routing).length) {
    out.push(`${Object.keys(config.model_routing).length} routing rule(s)`);
  }
  if (config.token_limits && Object.keys(config.token_limits).length) {
    out.push(`${Object.keys(config.token_limits).length} token budget(s)`);
  }
  if (config.dlp_rules && config.dlp_rules.length) {
    out.push(`${config.dlp_rules.length} DLP rule(s)`);
  }
  if (Array.isArray(config.policies)) {
    out.push(`${config.policies.length} policy record(s)`);
  }
  return out.length ? out : ['No structured rules captured in this snapshot'];
}

const PolicyVersionSelector: React.FC<PolicyVersionSelectorProps> = ({ apiUrl, value, onChange }) => {
  const { t } = useTranslation();
  const [versions, setVersions] = useState<PolicyVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPolicyVersions(apiUrl)
      .then((v) => { if (!cancelled) { setVersions(v); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiUrl]);

  const mode: 'inherit' | 'custom' = value ? 'custom' : 'inherit';
  const selected = versions.find((v) => v.id === value) || null;

  return (
    <div className="pv-select">
      <div className="pv-modes">
        <label className={`pv-mode ${mode === 'inherit' ? 'active' : ''}`}>
          <input type="radio" checked={mode === 'inherit'} onChange={() => onChange(null)} />
          <span>
            <strong>{t('components.policyVersionSelector.inheritPlatformDefault')}</strong>
            <span className="pv-mode-help">{t('components.policyVersionSelector.useWhateverPoliciesAreLiveWhen')}</span>
          </span>
        </label>
        <label className={`pv-mode ${mode === 'custom' ? 'active' : ''}`}>
          <input
            type="radio"
            checked={mode === 'custom'}
            onChange={() => onChange(versions[0]?.id || null)}
            disabled={!loading && versions.length === 0}
          />
          <span>
            <strong>{t('components.policyVersionSelector.pinAPolicyVersion')}</strong>
            <span className="pv-mode-help">{t('components.policyVersionSelector.freezeThisAgentToAnImmutable')}</span>
          </span>
        </label>
      </div>

      {loading && <p className="muted-text" style={{ margin: '8px 2px' }}>{t('components.policyVersionSelector.loadingPolicyVersions')}</p>}
      {error && <div className="mcp-select-error" style={{ margin: '8px 2px' }}><Icon name="warning" size={12} /> {error}</div>}

      {!loading && mode === 'custom' && (
        versions.length === 0 ? (
          <div className="notice info" style={{ marginTop: 10 }}>
            <Icon name="info" />
            {t('components.policyVersionSelector.noPolicyVersionsExistYetSnapshot')}
          </div>
        ) : (
          <div className="pv-list">
            {versions.map((v) => {
              const active = value === v.id;
              return (
                <button
                  type="button"
                  key={v.id}
                  className={`pv-row ${active ? 'active' : ''}`}
                  onClick={() => onChange(v.id)}
                >
                  <span className="pv-row-badge">{t('components.policyVersionSelector.v', { v0: v.version_number })}</span>
                  <span className="pv-row-info">
                    <span className="pv-row-name">{v.name || `Version ${v.version_number}`}</span>
                    <span className="pv-row-meta">
                      {parseServerDate(v.created_at).toLocaleDateString()} · {v.created_by}
                    </span>
                  </span>
                  {active && <Icon name="check" size={15} />}
                </button>
              );
            })}
          </div>
        )
      )}

      {selected && (
        <div className="pv-summary">
          <button type="button" className="pv-summary-toggle" onClick={() => setExpanded((e) => !e)}>
            <Icon name={expanded ? 'chevronDown' : 'chevronRight'} size={13} /> {t('components.policyVersionSelector.rulesInThisVersion')}
          </button>
          {expanded && (
            <ul className="pv-summary-list">
              {summarize(selected.config).map((line, i) => <li key={i}>{line}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default PolicyVersionSelector;
